// src/AdminRouteUpdate.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { auth, db } from "./firebase";
import { signOut } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

export default function AdminRouteUpdate() {
  const navigate = useNavigate();

  const buses = useMemo(
    () => Array.from({ length: 15 }, (_, i) => `bus${i + 1}`),
    []
  );

  const [leaveTime, setLeaveTime] = useState("4:10 PM");
  const [savingLeave, setSavingLeave] = useState(false);

  const [selectedBus, setSelectedBus] = useState("bus1");
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [savingRoute, setSavingRoute] = useState(false);

  // ✅ English only
  const [stopsText, setStopsText] = useState("");
  const [timesText, setTimesText] = useState("");

  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // load leave time
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "transport"));
        if (!alive) return;
        const d = snap.exists() ? snap.data() : {};
        setLeaveTime(d?.collegeLeaveTime || "4:10 PM");
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, []);

  const parseLines = (t = "") =>
    String(t)
      .split(/\r?\n/)
      .map((x) => x.trim())
      .filter(Boolean);

  const stopsArr = useMemo(() => parseLines(stopsText), [stopsText]);
  const timesArr = useMemo(() => parseLines(timesText), [timesText]);
  const countsOk = stopsArr.length > 0 && stopsArr.length === timesArr.length;

  const loadCurrent = async () => {
    setErr("");
    setMsg("");
    setLoadingRoute(true);

    try {
      const snap = await getDoc(doc(db, "buses", selectedBus));
      if (!snap.exists()) {
        setStopsText("");
        setTimesText("");
        setMsg("No route data found. Enter stops/times and save.");
        return;
      }
      const d = snap.data() || {};
      const s = Array.isArray(d.stops) ? d.stops : [];
      const t = Array.isArray(d.times) ? d.times : [];
      setStopsText(s.join("\n"));
      setTimesText(t.join("\n"));
      setMsg("Loaded current route.");
    } catch (e) {
      setErr(e?.message || "Failed to load current route.");
    } finally {
      setLoadingRoute(false);
    }
  };

  const saveLeaveTime = async () => {
    setErr("");
    setMsg("");
    setSavingLeave(true);
    try {
      await setDoc(
        doc(db, "settings", "transport"),
        {
          collegeLeaveTime: leaveTime.trim(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setMsg("Leave time saved.");
    } catch (e) {
      setErr(e?.message || "Failed to save leave time.");
    } finally {
      setSavingLeave(false);
    }
  };

  const saveRoute = async () => {
    setErr("");
    setMsg("");

    if (!selectedBus) return setErr("Select a bus.");
    if (stopsArr.length === 0) return setErr("Enter stops (English).");
    if (timesArr.length === 0) return setErr("Enter times (English).");
    if (stopsArr.length !== timesArr.length) {
      return setErr("Stops count must match Times count.");
    }

    setSavingRoute(true);
    try {
      await setDoc(
        doc(db, "buses", selectedBus),
        {
          stops: stopsArr,
          times: timesArr,

          // ✅ blink trigger for students
          routeUpdatedAt: serverTimestamp(),

          // general update markers
          updatedAt: serverTimestamp(),
          updatedAtMs: Date.now(),
        },
        { merge: true }
      );

      setMsg(`${selectedBus.toUpperCase()} route saved.`);
    } catch (e) {
      setErr(e?.message || "Failed to save route.");
    } finally {
      setSavingRoute(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch {}
    navigate("/admin-login", { replace: true });
  };

  return (
    <div className="admin-wrap">
      <style>{`
        .admin-wrap{
          min-height:100vh;
          padding: 28px 0 46px;
          background:
            radial-gradient(900px 600px at 15% 15%, rgba(47,123,255,.26), transparent 60%),
            radial-gradient(900px 600px at 85% 25%, rgba(255,77,109,.14), transparent 60%),
            radial-gradient(1000px 700px at 50% 95%, rgba(32,201,151,.12), transparent 60%),
            linear-gradient(180deg, #060a13 0%, #0b1220 100%);
        }
        .glass-card{
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.16);
          border-radius: 22px;
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          box-shadow: 0 18px 50px rgba(0,0,0,.42);
          color: #fff;
        }
        .subtxt{ color: rgba(234,240,255,.72); font-weight: 800; font-size: 13px; }

        .pill{
          border: 1px solid rgba(255,255,255,.14);
          background: rgba(255,255,255,.07);
          border-radius: 14px;
          padding: 12px 12px;
          color: rgba(255,255,255,.90);
        }
        .label{
          color: rgba(234,240,255,.72);
          font-weight: 900;
          letter-spacing: .6px;
          font-size: 12px;
          margin-bottom: 8px;
          text-transform: uppercase;
        }
        .inputx, .areax, .selectx{
          width: 100%;
          border-radius: 14px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.16);
          color: rgba(255,255,255,.92);
          padding: 12px 12px;
          outline: none;
        }
        .areax{
          min-height: 260px;
          resize: vertical;
          line-height: 1.7;
        }

        .btnx{
          border-radius: 14px;
          padding: 10px 14px;
          font-weight: 900;
          border: 0;
        }
        .btn-outlinex{
          border-radius: 14px;
          padding: 10px 14px;
          font-weight: 900;
          border: 1px solid rgba(255,255,255,.22);
          background: rgba(255,255,255,.06);
          color: rgba(255,255,255,.92);
        }
        .btn-outlinex:hover{ background: rgba(255,255,255,.12); }

        /* ✅ full color buttons */
        .btn-student{
          color:#071b10;
          background: linear-gradient(135deg, rgba(21,199,119,1), rgba(34,197,94,1));
          box-shadow: 0 14px 34px rgba(21,199,119,.22);
        }
        .btn-admin{
          color:#0b0214;
          background: linear-gradient(135deg, rgba(168,85,247,1), rgba(236,72,153,1));
          box-shadow: 0 14px 34px rgba(168,85,247,.20);
        }
        .btn-logout{
          color:#fff;
          background: linear-gradient(135deg, rgba(255,77,109,1), rgba(220,53,69,1));
          box-shadow: 0 14px 34px rgba(255,77,109,.18);
        }
        .btn-save{
          color:#fff;
          background: linear-gradient(135deg, rgba(47,123,255,1), rgba(106,166,255,1));
          box-shadow: 0 14px 34px rgba(47,123,255,.22);
        }

        .hr-soft{ border-color: rgba(255,255,255,.12); }
      `}</style>

      <div className="container">
        <div className="glass-card p-4">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h2 className="fw-bold m-0">Admin • Route Editor</h2>
              <div className="subtxt mt-1">
                Save → updates stops/times + routeUpdatedAt (blink trigger).
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btnx btn-student"
                onClick={() => navigate(`/track/${selectedBus}`)}
              >
                Student Preview
              </button>

              <button
                className="btnx btn-admin"
                onClick={() => navigate("/admin", { replace: true })}
              >
                Student List
              </button>

              <button className="btnx btn-logout" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>

          <hr className="hr-soft" />

          {err ? <div className="alert alert-danger mb-3">{err}</div> : null}
          {msg ? <div className="alert alert-success mb-3">{msg}</div> : null}

          <div className="row g-3">
            {/* left column */}
            <div className="col-12 col-lg-4">
              <div className="pill mb-3">
                <div className="label">Bus Leave College Time</div>
                <input
                  className="inputx"
                  value={leaveTime}
                  onChange={(e) => setLeaveTime(e.target.value)}
                  placeholder="4:10 PM"
                />
                <div className="d-grid mt-2">
                  <button
                    className="btnx btn-save"
                    onClick={saveLeaveTime}
                    disabled={savingLeave}
                  >
                    {savingLeave ? "Saving..." : "Save Leave Time"}
                  </button>
                </div>
              </div>

              <div className="pill mb-3">
                <div className="label">Select Bus</div>
                <select
                  className="selectx"
                  value={selectedBus}
                  onChange={(e) => setSelectedBus(e.target.value)}
                  disabled={loadingRoute || savingRoute}
                >
                  {buses.map((b) => (
                    <option key={b} value={b}>
                      {b.toUpperCase()}
                    </option>
                  ))}
                </select>

                <div className="d-flex gap-2 flex-wrap mt-2">
                  <button
                    className="btn-outlinex"
                    onClick={loadCurrent}
                    disabled={loadingRoute || savingRoute}
                  >
                    {loadingRoute ? "Loading..." : "Load Current"}
                  </button>

                  <button
                    className="btnx btn-save"
                    onClick={saveRoute}
                    disabled={savingRoute}
                  >
                    {savingRoute ? "Saving..." : "Save Route"}
                  </button>
                </div>
              </div>

              <div className="pill">
                <div className="label">Current Summary</div>
                <div style={{ fontWeight: 900, fontSize: 18 }}>
                  Stops: {stopsArr.length} • Times: {timesArr.length}
                </div>
                <div className="subtxt mt-2">
                  {countsOk
                    ? "Stops count matches Times count."
                    : "Stops count must match Times count."}
                </div>
              </div>
            </div>

            {/* right column */}
            <div className="col-12 col-lg-8">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <div className="label">Stops (English) • one per line</div>
                  <textarea
                    className="areax"
                    value={stopsText}
                    onChange={(e) => setStopsText(e.target.value)}
                    placeholder={"STOP1\nSTOP2\nSTOP3"}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <div className="label">Times (English) • one per line</div>
                  <textarea
                    className="areax"
                    value={timesText}
                    onChange={(e) => setTimesText(e.target.value)}
                    placeholder={"7:00 AM\n7:10 AM\n7:20 AM"}
                  />
                </div>
              </div>

              <div className="subtxt mt-3">
                Tamil stops/times removed (English only).
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}